import React from 'react';
import { PER_SEAT_CATEGORIES, rowTotal } from './invoicingSurface';

/**
 * Per Seat - Fixed invoicing sub-table: one row, the three booking categories as columns with
 * the company's call-outs beside them. Mirrors the Fixed CSV this row's download button
 * produces.
 *
 * There are no distance bands, and their absence IS the billing model: a fixed seat rate is
 * the same whatever the commute, so banding would invent a distinction the company is not
 * billed on. This is the Tiered table with its bands collapsed — literally so, since both
 * read the same payload, which is what guarantees a category here equals the sum of its nine
 * bands there.
 *
 * No leading label column either: the categories ARE the columns and there is only ever one
 * row, so a label would carry the same word on every render.
 *
 * @param {Object} props
 * @param {Object} props.perSeat - `toggle_table.per_seat`, keyed by row then by band.
 * @param {number|string} props.companyId - Used only for testids.
 * @returns {JSX.Element}
 */
const FixedPerSeatTable = ({ perSeat, companyId }) => {
  const columns = [
    ...PER_SEAT_CATEGORIES.map((category) => ({
      key: category.key,
      label: category.label,
      value: rowTotal(perSeat?.[category.key]),
    })),
    // Every late cancellation in the range, collapsed out of its bands exactly as the three
    // categories are. Equals the Total on the Tiered table's Call-Outs row.
    { key: 'call_outs', label: 'Call-Outs', value: rowTotal(perSeat?.call_outs) },
  ];

  return (
    <table className="table-fixed w-full border-collapse" data-testid={`invoicing-subtable-${companyId}`}>
      <thead>
        <tr>
          {columns.map((column) => (
            <th
              key={column.key}
              className="border-b p-2 text-center"
              data-testid={`invoicing-subth-${column.key}-${companyId}`}
            >
              {column.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          {columns.map((column) => (
            <td
              key={column.key}
              className="text-gray-700 border-t border-b p-2 text-center"
              data-testid={`invoicing-subtd-${column.key}-${companyId}`}
            >
              {column.value}
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );
};

export default FixedPerSeatTable;
